import { Close } from "@components/buttons/close";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { IRaffleDefaultRewardMediaDTO } from "@hooks/dtos/raffles/IRaffleDefaultDTO";
import { useEffect, useState } from "react";
import { FaPhotoFilm, FaVideo } from "react-icons/fa6";
import { FaTrashAlt } from "react-icons/fa";
import Swal from "sweetalert2";
import { youtubeExtractVideoId } from "@hooks/functions/Youtube/youtube";

interface IProps {
  medias: IRaffleDefaultRewardMediaDTO[];
  isOpen: boolean;
  onClose: (updatedMedias?: IRaffleDefaultRewardMediaDTO[]) => void;
}

export const ModalRaffleChangeRewardMedias = ({
  medias,
  isOpen,
  onClose,
}: IProps) => {
  const [animate, setAnimate] = useState(false);
  const [localMedias, setLocalMedias] = useState<
    IRaffleDefaultRewardMediaDTO[]
  >([]);
  const [selectedTab, setSelectedTab] = useState<number | null>(null);
  const [newMediaUrl, setNewMediaUrl] = useState("");
  const [newMediaType, setNewMediaType] = useState<"image" | "video">("image");
  const [newMediaDescription, setNewMediaDescription] = useState("");

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      setNewMediaUrl(base64String);
    };
    reader.readAsDataURL(file);
  };

  function handleClose(save: boolean = false) {
    setAnimate(false);
    setTimeout(() => {
      if (save) {
        onClose(localMedias);
      } else {
        onClose();
      }
    }, 300);
  }

  function handleEscape(evt: KeyboardEvent) {
    if (evt.key === "Escape" || evt.key === "Esc" || evt.keyCode === 27) {
      handleClose(false);
    }
  }

  function handleDragEnd(result: any) {
    if (!result.destination) return;

    const items = Array.from(localMedias);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updatedItems = items.map((item, index) => ({
      ...item,
      position: index + 1,
    }));

    setLocalMedias(updatedItems);
  }

  function handleAddMedia() {
    if (!newMediaUrl.trim()) return;

    let finalUrl = newMediaUrl;

    // Validate and extract YouTube ID if it's a video
    if (newMediaType === "video") {
      const videoId = youtubeExtractVideoId(newMediaUrl);
      if (!videoId) {
        Swal.fire({
          title: "Link inválido",
          text: "Por favor, insira um ID ou link válido do YouTube",
          icon: "error",
          confirmButtonText: "Ok",
        });
        return;
      }
      finalUrl = videoId;
    }

    const position = localMedias.length + 1;
    const newMedia: IRaffleDefaultRewardMediaDTO = {
      url: finalUrl,
      type: newMediaType,
      position,
      description: newMediaDescription || undefined,
      provider: newMediaType === "video" ? "youtube" : undefined,
    };

    setLocalMedias([...localMedias, newMedia]);
    setSelectedTab(newMedia.position);
    setNewMediaUrl("");
    setNewMediaDescription("");
  }

  function handleRemoveMedia(position: number) {
    const filtered = localMedias.filter((m) => m.position !== position);
    const reindexed = filtered.map((item, index) => ({
      ...item,
      position: index + 1,
    }));
    setLocalMedias(reindexed);
    if (selectedTab === position && reindexed.length > 0) {
      setSelectedTab(reindexed[0].position);
    }
  }

  function handleUpdateDescription(position: number, description: string) {
    setLocalMedias(
      localMedias.map((m) =>
        m.position === position
          ? { ...m, description: description || undefined }
          : m
      )
    );
  }

  function getMediaPreview(media: IRaffleDefaultRewardMediaDTO) {
    if (media.type === "video") {
      return `https://img.youtube.com/vi/${media.url}/default.jpg`;
    }
    // Check if already has data URI prefix
    if (media.url.startsWith("data:")) {
      return media.url;
    }
    // Add data URI prefix if it's base64
    return `data:image/jpeg;base64,${media.url}`;
  }

  const selectedMedia = localMedias.find((m) => m.position === selectedTab);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      return () => {
        document.removeEventListener("keydown", handleEscape);
      };
    }
  }, [isOpen, localMedias]);

  useEffect(() => {
    if (isOpen) {
      const sortedMedias = [...medias].sort((a, b) => a.position - b.position);
      setLocalMedias(sortedMedias);
      if (sortedMedias.length > 0) {
        setSelectedTab(sortedMedias[0].position);
      }
      setTimeout(() => setAnimate(true), 10);
    } else {
      setAnimate(false);
    }
  }, [isOpen, medias]);

  return (
    <>
      <div
        className={`modal fade ${animate && isOpen ? "show" : ""}`}
        style={{ display: isOpen ? "block" : "none" }}
        aria-labelledby="staticBackdropLabel"
        aria-hidden={!isOpen}
      >
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title fw-bold">Alterar Mídias do Prêmio</h5>
              <Close onPress={() => handleClose(false)} />
            </div>
            <div className="modal-body">
              <div className="row">
                {/* Left side: Media tabs with drag and drop */}
                <div className="col-md-4">
                  <h6 className="mb-3 fw-bold">Mídias (arraste para reordenar)</h6>
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="medias">
                      {(provided: any) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          className="list-group"
                          style={{ maxHeight: "400px", overflowY: "auto" }}
                        >
                          {localMedias.map((media, index) => {
                            const title =
                              media.type === "image" ? "Imagem" : "Vídeo";
                            const ico =
                              media.type === "video" ? (
                                <FaVideo className="fs-4" title={title} />
                              ) : (
                                <FaPhotoFilm className="fs-4" title={title} />
                              );
                            return (
                              <Draggable
                                key={media.position}
                                draggableId={String(media.position)}
                                index={index}
                              >
                                {(provided: any, snapshot: any) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className={`list-group-item d-flex align-items-center ${
                                      selectedTab === media.position
                                        ? "active"
                                        : ""
                                    } ${snapshot.isDragging ? "shadow" : ""}`}
                                    style={{
                                      ...provided.draggableProps.style,
                                      cursor: "grab",
                                      marginBottom: "8px",
                                    }}
                                    onClick={() =>
                                      setSelectedTab(media.position)
                                    }
                                  >
                                    <span className="badge bg-secondary me-2">
                                      {media.position}
                                    </span>
                                    <img
                                      src={getMediaPreview(media)}
                                      alt={`Media ${media.position}`}
                                      style={{
                                        width: "50px",
                                        height: "50px",
                                        objectFit: "cover",
                                        borderRadius: "4px",
                                        marginRight: "10px",
                                      }}
                                    />
                                    <div className="flex-grow-1 text-center">
                                      {ico}
                                    </div>
                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        Swal.fire({
                                          title: "Remover mídia?",
                                          text: "Esta ação não pode ser desfeita",
                                          icon: "warning",
                                          showCancelButton: true,
                                          confirmButtonColor: "#d33",
                                          cancelButtonColor: "#3085d6",
                                          confirmButtonText: "Sim, remover!",
                                          cancelButtonText: "Cancelar",
                                        }).then(
                                          (result: {
                                            isConfirmed: boolean;
                                          }) => {
                                            if (result.isConfirmed) {
                                              handleRemoveMedia(media.position);
                                            }
                                          }
                                        );
                                      }}
                                    >
                                      <FaTrashAlt className="fs-5" />
                                    </button>
                                  </div>
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>

                  {/* Add new media */}
                  <div className="mt-3">
                    <h6 className="fw-bold">Adicionar Nova Mídia</h6>
                    <div className="mb-2">
                      <select
                        className="form-select form-select-sm"
                        value={newMediaType}
                        onChange={(e) =>
                          setNewMediaType(e.target.value as "image" | "video")
                        }
                      >
                        <option value="image">Imagem</option>
                        <option value="video">Vídeo</option>
                      </select>
                    </div>
                    <div className="mb-2">
                      {newMediaType === "image" ? (
                        <input
                          type="file"
                          className="form-control form-control-sm"
                          accept="image/*"
                          onChange={handleImageUpload}
                        />
                      ) : (
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Cole o ID do vídeo (ex: RWJ8xyFAsOo)"
                          value={newMediaUrl}
                          onChange={(e) => setNewMediaUrl(e.target.value)}
                        />
                      )}
                    </div>
                    <div className="mb-2">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Descrição (opcional)"
                        value={newMediaDescription}
                        onChange={(e) => setNewMediaDescription(e.target.value)}
                      />
                    </div>
                    <button
                      className="btn btn-sm btn-primary w-100"
                      onClick={handleAddMedia}
                    >
                      <i className="bi bi-plus-circle me-1"></i>
                      Adicionar
                    </button>
                  </div>
                </div>

                {/* Right side: Selected media preview and details */}
                <div className="col-md-8">
                  {selectedMedia ? (
                    <div>
                      <span className="badge bg-info fs-6 p-2 mb-2">
                        Preview: Posição {selectedMedia.position}
                      </span>
                      <div
                        className="mb-3"
                        style={{
                          border: "1px solid #dee2e6",
                          borderRadius: "8px",
                          padding: "15px",
                          backgroundColor: "#f8f9fa",
                        }}
                      >
                        {selectedMedia.type === "video" ? (
                          <div
                            style={{
                              position: "relative",
                              paddingBottom: "56.25%",
                            }}
                          >
                            <iframe
                              src={`https://www.youtube.com/embed/${selectedMedia.url}`}
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                borderRadius: "4px",
                              }}
                              allowFullScreen
                            ></iframe>
                          </div>
                        ) : (
                          <img
                            src={getMediaPreview(selectedMedia)}
                            alt={`Media ${selectedMedia.position}`}
                            style={{
                              width: "100%",
                              maxHeight: "400px",
                              objectFit: "contain",
                              borderRadius: "4px",
                            }}
                          />
                        )}
                      </div>
                      <div>
                        <label className="form-label">
                          <strong>Descrição</strong>
                        </label>
                        <textarea
                          className="form-control"
                          rows={3}
                          value={selectedMedia.description || ""}
                          onChange={(e) =>
                            handleUpdateDescription(
                              selectedMedia.position,
                              e.target.value
                            )
                          }
                          placeholder="Adicione uma descrição para esta mídia..."
                        />
                      </div>
                      <div className="mt-3">
                        <small className="text-muted">
                          <strong>Tipo:</strong> {selectedMedia.type}
                          {selectedMedia.provider && (
                            <>
                              {" | "}
                              <strong>Provider:</strong>{" "}
                              {selectedMedia.provider}
                            </>
                          )}
                        </small>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-muted mt-5">
                      <i
                        className="bi bi-images"
                        style={{ fontSize: "3rem" }}
                      ></i>
                      <p>Selecione ou adicione uma mídia</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-success"
                onClick={() => handleClose(true)}
              >
                Salvar Alterações
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => handleClose(false)}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`modal-backdrop fade ${animate && isOpen ? "show" : ""}`}
        style={{ display: isOpen ? "block" : "none" }}
      ></div>
    </>
  );
};
